import { useState } from 'react';

import { BannerPlacement } from '../../../../typings/enums';
import { window } from '../../../globals';
import { hardRedirectTo } from '../../../utils';
import BannerManager from '../../BannerManager';
import SignInSignUpToggle from '../components/SignInSignUpToggle';
import useAuthState from '../hooks/useAuthState';
import AuthLayout from '../layout/AuthLayout';
import EnterIdentifierStep from '../steps/EnterIdentifierStep';
import MethodsStep from '../steps/MethodsStep';
import OrgPickerStep from '../steps/OrgPickerStep';
import type { Candidate, ResolveUserResponse, SignupSuggestion } from '../../../../shared/types/routes/auth/v2';
import type { RouterProps } from '../../../types';

export type LoginPageProps = {
  location: RouterProps['location'];
};

export type LoginStep =
  | { kind: 'enter_identifier' }
  | { kind: 'pick_org'; candidates: Candidate[]; signupSuggestions: SignupSuggestion[] }
  | { kind: 'methods'; org: Candidate };

const Login = ({ location }: LoginPageProps) => {
  const [identifier, setIdentifier] = useState(location.query.email ?? '');
  const [step, setStep] = useState<LoginStep>({ kind: 'enter_identifier' });
  const { data: authState } = useAuthState();

  const oauthProviders = authState?.kind === 'idle' ? authState.oauthProviders : [];

  const handleResolved = (response: ResolveUserResponse) => {
    const { candidates, signupSuggestions } = response;
    const [firstCandidate] = candidates;

    if (firstCandidate && candidates.length === 1 && signupSuggestions.length === 0) {
      setStep({ kind: 'methods', org: firstCandidate });
    } else if (candidates.length > 0 || signupSuggestions.length > 0) {
      setStep({ kind: 'pick_org', candidates, signupSuggestions });
    } else {
      hardRedirectTo(`/auth/register?email=${encodeURIComponent(identifier)}`);
    }
  };

  const advanceToMethods = (org: Candidate) => setStep({ kind: 'methods', org });
  const resetToIdentifier = () => setStep({ kind: 'enter_identifier' });

  const renderStep = () => {
    switch (step.kind) {
      case 'enter_identifier':
        return (
          <EnterIdentifierStep
            identifier={identifier}
            onIdentifierChange={setIdentifier}
            oauthProviders={oauthProviders}
            onResolved={handleResolved}
          />
        );
      case 'pick_org':
        return (
          <OrgPickerStep
            candidates={step.candidates}
            signupSuggestions={step.signupSuggestions}
            onPickCandidate={advanceToMethods}
            onBack={resetToIdentifier}
          />
        );
      case 'methods':
        return <MethodsStep org={step.org} />;
      default: {
        const exhaustiveCheck: never = step;
        return exhaustiveCheck;
      }
    }
  };

  return (
    <AuthLayout>
      {renderStep()}
      <SignInSignUpToggle identifier={identifier} next={location.query.next} />
      {window.on_prem && <BannerManager placement={BannerPlacement.Login} />}
    </AuthLayout>
  );
};

export default Login;
